import { Component, Inject, OnDestroy, signal, effect } from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { OrderUtilsService } from '../shared/order-utils.service';
import { MessageService } from '../../shared/message.service';
import { AccountService } from '../../login/account.service';
import { WarehouseService } from '../../shared/service/warehouse-service';
import { IWarehouse } from '../../shared/model/warehouse.model';


@Component({
  selector: 'app-confirm-order-dialog',
  imports: [
    CommonModule,
    NgIf,
    MatDialogActions,
    MatDialogContent
  ],
  templateUrl: './confirm-order-dialog.html',
  styleUrl: './confirm-order-dialog.css'
})
export class ConfirmOrderDialog implements OnDestroy{

  currentStep: 'confirm' | 'processing' | 'success' | 'error' = 'success';
  orderNumber: string | null = null;
  private closedManually = false;
  activeWarehouse = signal<IWarehouse | null>(null);

  constructor(
    public dialogRef: MatDialogRef<ConfirmOrderDialog>,
    private router: Router,
    private orderUtils: OrderUtilsService,
    private messageService: MessageService,
    private accountService: AccountService,
    private warehouseService: WarehouseService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.currentStep = this.data?.mode === 'confirm' ? 'confirm' : 'success';
    this.orderNumber = this.data?.orderNumber ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('orderNumber') : null);
    this.dialogRef.backdropClick().subscribe(() => this.handleUncontrolledClose());
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') this.handleUncontrolledClose();
    });

    effect(() => {
      const warehouseCode = this.accountService.selectedWarehouseCode();
      if (warehouseCode) {
        this.loadWarehouseInfo(warehouseCode);
      } else {
        this.activeWarehouse.set(null);
      }
    });
  }

  ngOnDestroy() {
    if (!this.closedManually && this.currentStep === 'success') {
      this.router.navigate(['/home/pedidos']);
    }
  }

  private handleUncontrolledClose() {
    this.closedManually = true;
    if (this.currentStep === 'success') {
      this.goHome();
    } else {
      this.dialogRef.close(false);
    }
  }

  close() {
    this.dialogRef.close(false);
  }

  confirm() {
    if (!this.data || typeof this.data.onConfirm !== 'function') {
      this.messageService.showError('No se pudo confirmar el pedido');
      return;
    }

    this.currentStep = 'processing';
    try {
      const result = this.data.onConfirm();

      // Observable-like
      if (result && typeof result.subscribe === 'function') {
        result.subscribe({
          next: (response: any) => {
            this.handleSaveResponseInDialog(response);
            this.messageService.showSuccess('Pedido guardado y facturado');
            this.currentStep = 'success';
          },
          error: () => {
            this.currentStep = 'confirm';
          }
        });
        return;
      }

      // Promise-like
      if (result && typeof result.then === 'function') {
        (result as Promise<any>)
          .then((response: any) => {
            this.handleSaveResponseInDialog(response);
            this.messageService.showSuccess('Pedido guardado y facturado');
            this.currentStep = 'success';
          })
          .catch(() => {
            this.messageService.showError('Ocurrió un error al confirmar el pedido');
            this.currentStep = 'confirm';
          });
        return;
      }

      // Invalid callback return
      this.messageService.showError('No se pudo confirmar el pedido');
      this.currentStep = 'confirm';
    } catch {
      this.messageService.showError('Ocurrió un error al confirmar el pedido');
      this.currentStep = 'confirm';
    }
  }

  goHome() {
    this.dialogRef.close(true);
    this.router.navigate(['/home/pedidos']);
  }

  downloadOrder() {
    const orderId = localStorage.getItem('orderId');

    if (!orderId) {
      console.error('No orderId found in localStorage');
      this.messageService.showError('No se encontró el número de pedido');
      return;
    }

    this.orderUtils.downloadOrderExcel(orderId);
  }

  private handleSaveResponseInDialog(response: any) {
    let payload = response;
    if (response && response.body && typeof response.body === 'string') {
      try {
        payload = JSON.parse(response.body);
      } catch {
        // ignore parse error
      }
    } else if (response && response.body && typeof response.body === 'object') {
      payload = response.body;
    }

    const returnedOrderId = payload?.orderId
      ?? payload?.order?.order?.[0]?.id
      ?? payload?.order?.id
      ?? payload?.id
      ?? null;
    if (returnedOrderId) localStorage.setItem('orderId', String(returnedOrderId));

    const returnedOrderNumber = payload?.orderNumber
      ?? payload?.order?.order?.[0]?.orderNumber
      ?? payload?.order?.order?.[0]?.order_number
      ?? payload?.order?.orderNumber
      ?? payload?.order?.order_number
      ?? payload?.order?.order?.[0]?.order_number
      ?? payload?.order?.orderNumber
      ?? null;

    if (returnedOrderNumber) {
      this.orderNumber = returnedOrderNumber;
      localStorage.setItem('orderNumber', String(returnedOrderNumber));
    }
  }

  private loadWarehouseInfo(code: string) {
    this.warehouseService.findByCode(code).subscribe({
      next: (res) => {
        const responseBody = res.body as any;
        const warehouse = responseBody?.warehouse as IWarehouse;
        this.activeWarehouse.set(warehouse || null);
      },
      error: () => {
        this.activeWarehouse.set(null);
      }
    });
  }


}
